import * as THREE from "three";
import { setTerrainHeightProvider } from "./terrain";
import { paintFarFieldVertexColors } from "./terrain-materials";
import { addHorizonRing } from "./horizon";
import { ARCTIC_WOLF_ROUTE } from "./nav";

// ARCTIC WOLF (map06) — Theater 5 in THEME.md: northern Norway in perpetual
// grey twilight. Snowfields with dark rock breaking through on the steep
// faces (the alpine read: slope decides, not just altitude), pine bands
// below the treeline, a frozen lake the route crosses ON the ice, and a
// frozen fjord past the shore LZ. Stylized-only.

const ROUTE = ARCTIC_WOLF_ROUTE.map((n) => n.position);

// The frozen lake the route crosses at its third node.
const LAKE: [number, number] = [-80, -60];
const LAKE_RADIUS = 95;
export const ICE_LEVEL = -0.35;

// The fjord: everything past the shore diagonal descends under the ice.
// The LZ (350,300 → sum 650) sits on the shore; ice starts at 820.
export function fjordShoreAt(x: number, z: number): number {
  return (x + z - 820) / Math.SQRT2; // positive = out on the fjord
}

function smoothNoise(x: number, z: number, scale: number): number {
  return (
    Math.sin(x * scale) * 0.42 +
    Math.cos(z * scale * 1.17) * 0.36 +
    Math.sin((x + z) * scale * 0.63) * 0.22
  ) * 0.5 + 0.5;
}

function distanceToSegment(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const abx = bx - ax;
  const abz = bz - az;
  const lenSq = abx * abx + abz * abz;
  const t = lenSq <= 1e-6 ? 0 : THREE.MathUtils.clamp(((px - ax) * abx + (pz - az) * abz) / lenSq, 0, 1);
  return Math.hypot(px - (ax + abx * t), pz - (az + abz * t));
}

export function map06RouteDistanceAt(x: number, z: number): number {
  let best = Infinity;
  for (let i = 0; i < ROUTE.length - 1; i++) {
    const a = ROUTE[i]!;
    const b = ROUTE[i + 1]!;
    best = Math.min(best, distanceToSegment(x, z, a.x, a.z, b.x, b.z));
  }
  return best;
}

export function lakeDistanceAt(x: number, z: number): number {
  return Math.hypot(x - LAKE[0], z - LAKE[1]);
}

// Alpine relief: strong ridges rising toward the edges, a graded route
// valley, the lake basin carved under its ice, the fjord dropping away
// past the shore.
export function map06HeightAt(x: number, z: number): number {
  const centerDist = Math.hypot(x, z);
  let height = 6
    + (smoothNoise(x + 70, z - 50, 0.0045) - 0.5) * 42
    + (smoothNoise(x - 110, z + 90, 0.012) - 0.5) * 10
    + THREE.MathUtils.smoothstep(centerDist, 380, 1000) * (90 + smoothNoise(x, z, 0.003) * 130);

  // The route is a walkable valley through the mountains
  const routeDist = map06RouteDistanceAt(x, z);
  const corridor = 1 - THREE.MathUtils.smoothstep(routeDist, 24, 110);
  height = THREE.MathUtils.lerp(height, 3.5 + (smoothNoise(x, z, 0.02) - 0.5) * 2.8, corridor);

  // The lake basin sits under its ice sheet
  const lake = 1 - THREE.MathUtils.smoothstep(lakeDistanceAt(x, z), LAKE_RADIUS * 0.75, LAKE_RADIUS);
  height = THREE.MathUtils.lerp(height, -2.4, lake);

  // The fjord drops away past the shore
  const fjord = THREE.MathUtils.smoothstep(fjordShoreAt(x, z), 0, 70);
  height = THREE.MathUtils.lerp(height, -7, fjord);

  const edgeDist = Math.max(Math.abs(x), Math.abs(z));
  const edgeFade = 1 - THREE.MathUtils.smoothstep(edgeDist, 960, 1090);
  // The fjord corner keeps its depth out to the edge — the ice covers it
  const floor = fjord > 0.5 ? -7 : -2;
  return THREE.MathUtils.lerp(floor, height, edgeFade);
}

function slopeAt(x: number, z: number): number {
  const d = 4;
  const h0 = map06HeightAt(x, z);
  const dx = (map06HeightAt(x + d, z) - h0) / d;
  const dz = (map06HeightAt(x, z + d) - h0) / d;
  return Math.hypot(dx, dz);
}

// Palette: snow whites kept off pure-white (headroom for the key), dark
// rock on the steep faces, cold shadow blues in the low ground.
const SNOW_BRIGHT = new THREE.Color(0xdde3e8);
const SNOW_SHADE = new THREE.Color(0xbfc9d4);
const SNOW_HOLLOW = new THREE.Color(0xaab6c4);
const ROCK_DARK = new THREE.Color(0x4a4e54);
const ROCK_SCREE = new THREE.Color(0x6a6e74);
const PINE_FLOOR = new THREE.Color(0x8d9aa0);
const TRACK_PACKED = new THREE.Color(0xaeb9c2);
const ICE_PAINT = new THREE.Color(0xc6d6e0);

function paintMap06Ground(geo: THREE.PlaneGeometry): void {
  const pos = geo.attributes["position"] as THREE.BufferAttribute;
  const colors = new Float32Array(pos.count * 3);
  const color = new THREE.Color();

  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const h = pos.getY(i);
    const grain = smoothNoise(x, z, 0.026);

    // Snow shades by altitude: hollows blue, high fields bright
    color.copy(SNOW_HOLLOW).lerp(SNOW_SHADE, THREE.MathUtils.smoothstep(h, 0, 30));
    color.lerp(SNOW_BRIGHT, THREE.MathUtils.smoothstep(h, 30, 110));

    // The alpine read: steep faces shed their snow — dark rock bands
    const slope = slopeAt(x, z);
    const rock = THREE.MathUtils.smoothstep(slope, 0.55, 0.95);
    if (rock > 0) {
      color.lerp(ROCK_DARK, rock * 0.85);
      color.lerp(ROCK_SCREE, rock * grain * 0.35);
    }

    // Slightly dirty snow under the pine bands (painted even where trees
    // are sparse — ties the scatter to the ground)
    if (h > 4 && h < 55 && slope < 0.4 && grain > 0.55) {
      color.lerp(PINE_FLOOR, 0.3);
    }

    // The frozen lake reads as ice in the paint too (the disc sits above)
    const lake = 1 - THREE.MathUtils.smoothstep(lakeDistanceAt(x, z), LAKE_RADIUS * 0.7, LAKE_RADIUS);
    if (lake > 0) color.lerp(ICE_PAINT, lake * 0.8);

    // Packed-snow track
    const routeDist = map06RouteDistanceAt(x, z);
    const track = 1 - THREE.MathUtils.smoothstep(routeDist, 4, 10);
    if (track > 0) color.lerp(TRACK_PACKED, track * 0.85);

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;
  }
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
}

const GROUND_MATERIAL = new THREE.MeshStandardMaterial({
  vertexColors: true,
  flatShading: true,
  roughness: 1.0,
  metalness: 0,
});

// --- Ice sheets ---------------------------------------------------------------
// Flat pale discs/planes at ICE_LEVEL: the lake, and the fjord filling its
// carved corner. Solid color — stylized ice, no shader.
const ICE_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0xc9dae6,
  roughness: 0.35,
  metalness: 0,
});

function addIce(parent: THREE.Group, size: number): void {
  const lake = new THREE.Mesh(new THREE.CircleGeometry(LAKE_RADIUS, 40), ICE_MATERIAL);
  lake.rotation.x = -Math.PI / 2;
  lake.position.set(LAKE[0], ICE_LEVEL, LAKE[1]);
  lake.name = "map06-lake-ice";
  lake.receiveShadow = true;
  lake.userData["losTransparent"] = true;
  lake.userData["ignoreBallistics"] = true;
  parent.add(lake);

  // Fjord sheet: a world-space quad laid along the shore diagonal, from
  // just past the shoreline out beyond the map edge. size kept for parity
  // with the terrain extent.
  const half = size * 0.9;
  const shore = new THREE.Vector2(410, 410); // closest shoreline point to map center
  const along = new THREE.Vector2(1, -1).normalize(); // shoreline direction
  const out = new THREE.Vector2(1, 1).normalize(); // out onto the fjord
  const corner = (a: number, o: number): [number, number, number] => [
    shore.x + along.x * a + out.x * o,
    ICE_LEVEL,
    shore.y + along.y * a + out.y * o,
  ];
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute([
    ...corner(-half, 6), ...corner(half, 6), ...corner(half, 900), ...corner(-half, 900),
  ], 3));
  geo.setIndex([0, 2, 1, 0, 3, 2]);
  geo.computeVertexNormals();
  const fjord = new THREE.Mesh(geo, ICE_MATERIAL);
  fjord.name = "map06-fjord-ice";
  fjord.receiveShadow = true;
  fjord.userData["losTransparent"] = true;
  fjord.userData["ignoreBallistics"] = true;
  parent.add(fjord);
}

const TRACK_MATERIAL = new THREE.MeshStandardMaterial({
  color: 0xa7b2bc,
  roughness: 0.9,
  metalness: 0,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -2,
});

function resamplePolyline(points: THREE.Vector3[], step: number): THREE.Vector3[] {
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const segments = Math.max(1, Math.ceil(a.distanceTo(b) / step));
    for (let s = 0; s < segments; s++) out.push(a.clone().lerp(b, s / segments));
  }
  out.push(points[points.length - 1]!.clone());
  return out;
}

// Upward-wound ground ribbon (the hard-learned winding: base, base+2, base+1)
function makeTrackRibbon(points: THREE.Vector3[], width: number): THREE.Mesh {
  const positions: number[] = [];
  const indices: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    const forward = new THREE.Vector3(next.x - prev.x, 0, next.z - prev.z).normalize();
    const right = new THREE.Vector3(forward.z, 0, -forward.x);
    const p = points[i]!;
    const lx = p.x - right.x * width * 0.5;
    const lz = p.z - right.z * width * 0.5;
    const rx = p.x + right.x * width * 0.5;
    const rz = p.z + right.z * width * 0.5;
    // The ribbon rides the terrain but never dips under the lake ice
    positions.push(lx, Math.max(map06HeightAt(lx, lz), ICE_LEVEL) + 0.16, lz);
    positions.push(rx, Math.max(map06HeightAt(rx, rz), ICE_LEVEL) + 0.16, rz);
    if (i < points.length - 1) {
      const base = i * 2;
      indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const track = new THREE.Mesh(geo, TRACK_MATERIAL);
  track.name = "map06-track";
  track.receiveShadow = true;
  track.userData["losTransparent"] = true;
  return track;
}

// --- Pines --------------------------------------------------------------------
function scatterRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) | 0;
    return (s >>> 0) / 0xffffffff;
  };
}

// Dark pine bands below the treeline — the only saturated color on the map,
// which is exactly why they read.
function addPines(parent: THREE.Group): void {
  const count = 5200;
  const material = new THREE.MeshStandardMaterial({ flatShading: true, roughness: 0.95, metalness: 0 });
  const mesh = new THREE.InstancedMesh(new THREE.ConeGeometry(1, 3.2, 6), material, count);
  const rnd = scatterRng(0x0a5c71);
  const matrix = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const colorA = new THREE.Color(0x1d3026);
  const colorB = new THREE.Color(0x2e4736);
  const color = new THREE.Color();
  let placed = 0;
  let attempts = 0;
  while (placed < count && attempts < count * 40) {
    attempts++;
    const x = (rnd() - 0.5) * 2040;
    const z = (rnd() - 0.5) * 2040;
    if (Math.max(Math.abs(x), Math.abs(z)) > 920) continue;
    const h = map06HeightAt(x, z);
    if (h < 1 || h > 62) continue; // treeline band
    if (slopeAt(x, z) > 0.7) continue; // not on cliff faces
    if (map06RouteDistanceAt(x, z) < 20) continue;
    if (lakeDistanceAt(x, z) < LAKE_RADIUS + 8) continue;
    if (fjordShoreAt(x, z) > -30) continue;
    // Banded density — groves, not an even sprinkle
    if (smoothNoise(x + 300, z - 140, 0.008) < 0.45) continue;
    const s = 3.2 + rnd() * 2.6;
    quat.setFromAxisAngle(up, rnd() * Math.PI * 2);
    matrix.compose(new THREE.Vector3(x, h + s * 1.35, z), quat, new THREE.Vector3(s, s, s));
    mesh.setMatrixAt(placed, matrix);
    color.copy(colorA).lerp(colorB, rnd());
    mesh.setColorAt(placed, color);
    placed++;
  }
  mesh.count = placed;
  mesh.name = "map06-pines";
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.frustumCulled = false;
  mesh.userData["losTransparent"] = true;
  mesh.userData["ignoreBallistics"] = true;
  parent.add(mesh);
}

// The fishing settlement at the fjord shore — dark-roofed cabins around the
// LZ, plus the cut-off team's position on the ridge.
const CABIN_WALL = new THREE.MeshStandardMaterial({ color: 0x6e4a3a, flatShading: true, roughness: 0.95 });
const CABIN_ROOF = new THREE.MeshStandardMaterial({ color: 0x3a3e42, flatShading: true, roughness: 1.0 });

function addCabin(parent: THREE.Group, x: number, z: number, rot: number, rnd: () => number): void {
  const w = 7 + rnd() * 4;
  const d = 5.5 + rnd() * 3;
  const h = 3.4;
  const cabin = new THREE.Group();
  const body = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), CABIN_WALL);
  body.position.y = h / 2;
  const roof = new THREE.Mesh(new THREE.CylinderGeometry(0.1, d * 0.75, w, 3), CABIN_ROOF);
  roof.rotation.z = Math.PI / 2;
  roof.rotation.y = Math.PI / 2;
  roof.position.y = h + 1.2;
  cabin.add(body, roof);
  cabin.position.set(x, map06HeightAt(x, z), z);
  cabin.rotation.y = rot;
  cabin.children.forEach((c) => {
    c.castShadow = true;
    c.receiveShadow = true;
  });
  parent.add(cabin);
}

function addSettlements(parent: THREE.Group): void {
  const rnd = scatterRng(0x77aa19);
  const clusters: Array<{ cx: number; cz: number; n: number; r: number }> = [
    { cx: 355, cz: 310, n: 6, r: 55 }, // fjord shore settlement at the LZ
    { cx: -390, cz: -345, n: 3, r: 36 }, // the cut-off position
  ];
  for (const c of clusters) {
    for (let i = 0; i < c.n; i++) {
      const angle = rnd() * Math.PI * 2;
      const dist = 14 + rnd() * c.r;
      const x = c.cx + Math.cos(angle) * dist;
      const z = c.cz + Math.sin(angle) * dist;
      if (map06RouteDistanceAt(x, z) < 14 || fjordShoreAt(x, z) > -6) continue;
      addCabin(parent, x, z, rnd() * Math.PI * 2, rnd);
    }
  }
}

export class Map06Terrain {
  readonly group = new THREE.Group();

  constructor(size = 2200) {
    setTerrainHeightProvider(map06HeightAt);
    const geo = new THREE.PlaneGeometry(size, size, 260, 260);
    geo.rotateX(-Math.PI / 2);
    const pos = geo.attributes["position"] as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, map06HeightAt(pos.getX(i), pos.getZ(i)));
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();
    paintMap06Ground(geo);
    const ground = new THREE.Mesh(geo, GROUND_MATERIAL);
    ground.name = "map06-ground";
    ground.receiveShadow = true;
    this.group.add(ground);

    addIce(this.group, size);
    this.group.add(makeTrackRibbon(resamplePolyline(ROUTE.map((v) => v.clone()), 12), 10));
    addPines(this.group);
    addSettlements(this.group);
    this.addFarField(size);
    // Grey-twilight horizon: snowbound ridges into pale sky
    addHorizonRing(this.group, size, { ridge: 0x707a86, haze: 0xbcc5ce }, 0x1f10cd3);
  }

  private addFarField(size: number): void {
    const geo = new THREE.CircleGeometry(size * 1.7, 96);
    geo.rotateX(-Math.PI / 2);
    paintFarFieldVertexColors(geo, size * 0.5, { inner: 0xc3ccd4, outer: 0xcdd5dc, dry: 0xb4bfc9 });
    const disc = new THREE.Mesh(
      geo,
      new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1, metalness: 0 }),
    );
    disc.name = "map06-farfield";
    disc.position.y = -2.2;
    disc.receiveShadow = true;
    disc.userData["losTransparent"] = true;
    this.group.add(disc);
  }
}
